import React from "react";
import {
  Check,
  Copy,
  ExternalLink,
  Facebook,
  MessageSquare,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export type SiteLiveModalProps = {
  open: boolean;
  siteUrl: string;
  onClose: () => void;
};

type CopyState = "idle" | "copied";

function buildShareLinks(siteUrl: string) {
  const encoded = encodeURIComponent(siteUrl);
  const smsBody = encodeURIComponent(`Check out my website: ${siteUrl}`);
  const waText = encodeURIComponent(`Check out my website: ${siteUrl}`);
  return {
    sms: `sms:?body=${smsBody}`,
    whatsapp: `https://wa.me/?text=${waText}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
  };
}

export const SiteLiveModal: React.FC<SiteLiveModalProps> = ({
  open,
  siteUrl,
  onClose,
}) => {
  const [copyState, setCopyState] = React.useState<CopyState>("idle");
  const shareLinks = buildShareLinks(siteUrl);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      /* clipboard blocked */
    }
  };

  const handleShare = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3 mx-auto">
            <Check className="w-6 h-6 text-emerald-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            Your site is live
          </DialogTitle>
          <DialogDescription className="sr-only">
            Share {siteUrl}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border bg-muted/40 p-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5 font-medium">
              Your website
            </p>
            <div className="flex items-center gap-2">
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-sm font-medium text-foreground truncate hover:underline"
              >
                {siteUrl}
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                className="shrink-0 gap-1.5"
              >
                {copyState === "copied" ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-2.5 text-center">
              Share it now:
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-col gap-1 h-auto py-3"
                onClick={() => handleShare(shareLinks.sms)}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="text-xs">SMS</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-col gap-1 h-auto py-3"
                onClick={() => handleShare(shareLinks.whatsapp)}
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-xs">WhatsApp</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-col gap-1 h-auto py-3"
                onClick={() => handleShare(shareLinks.facebook)}
              >
                <Facebook className="w-4 h-4" />
                <span className="text-xs">Facebook</span>
              </Button>
            </div>
          </div>
        </div>

        <Button onClick={onClose} className="w-full mt-1">
          Start exploring your dashboard
        </Button>
      </DialogContent>
    </Dialog>
  );
};
